// v3.9.36 — curated Philippine post taxonomy (CEO #3, #4, #6). Powers the
// "dumb-proof" create-post form: pick a category, then pick a Title/role from a
// suggested list (still free-text via "Other"), with click-to-add Tags and
// suggestive Description starters. Focused on the PH construction/trades/services
// market. The legacy mixed title list still serves profile callers; posting uses
// titlesForPosting to distinguish roles, service scopes, projects and RFQ needs.

export interface CategoryDef {
  key: string;
  label: string;
  titles: string[];
  tags: string[];
  descriptions: string[];
}

export const POST_CATEGORIES: CategoryDef[] = [
  {
    key: "construction", label: "Construction & Civil Works",
    titles: ["General Construction", "Building Contractor", "Civil Works", "Concrete Works & Pouring", "Foundation & Excavation", "Structural Retrofitting", "Demolition Works", "Roadworks & Pavement", "Fit-out & Renovation", "Site Formworks & Scaffolding", "Construction Foreman", "Construction Laborer"],
    tags: ["Construction", "Renovation", "Concrete", "Civil Works", "Contractor", "Foundation", "Demolition", "Fit-out"],
    descriptions: [
      "We handle the full scope from site preparation to turnover, with our own crew and equipment.",
      "Experienced in residential and commercial builds across Metro Manila and nearby provinces.",
      "Free site assessment and detailed quotation. Materials and labor can be quoted separately.",
    ],
  },
  {
    key: "electrical", label: "Electrical",
    titles: ["Electrician", "Electrical Installation", "House Rewiring", "Panel Board Upgrade", "Generator Installation", "CCTV & Alarm Wiring", "Solar Panel Installation", "Electrical Maintenance", "Lighting Installation", "Licensed Master Electrician"],
    tags: ["Electrical", "Wiring", "Rewiring", "Panel Board", "CCTV", "Solar", "Lighting", "Maintenance"],
    descriptions: [
      "Licensed electrical work compliant with the Philippine Electrical Code. Safe, inspected, and warranted.",
      "From new installations to troubleshooting and rewiring — residential and commercial.",
      "We can supply and install, or work with your provided materials.",
    ],
  },
  {
    key: "plumbing", label: "Plumbing & Sanitary",
    titles: ["Plumber", "Plumbing Installation", "Pipe Repair & Leak Fixing", "Water Heater Installation", "Septic & Drainage Works", "Bathroom & Toilet Fit-out", "Water Tank & Pump Setup", "Sanitary Maintenance"],
    tags: ["Plumbing", "Sanitary", "Leak Repair", "Drainage", "Water Heater", "Pipes", "Septic", "Maintenance"],
    descriptions: [
      "Fast, clean plumbing work — leaks, clogs, installations, and full roughing-in.",
      "We diagnose the root cause first, then quote so there are no surprises.",
      "Emergency leak and clog service available in the metro.",
    ],
  },
  {
    key: "carpentry", label: "Carpentry & Masonry",
    titles: ["Carpenter", "Mason", "Tile Setter", "Cabinet Making", "Drywall & Ceiling Works", "Concrete Hollow Block Laying", "Formworks Carpentry", "Wood Flooring Installation", "Door & Window Fitting"],
    tags: ["Carpentry", "Masonry", "Tiling", "Cabinets", "Ceiling", "Drywall", "Flooring", "Finishing"],
    descriptions: [
      "Precise carpentry and masonry with clean finishing and honest timelines.",
      "Custom cabinets, tiling, ceilings, and concrete works for homes and shops.",
      "Workmanship warranty included. Materials on your account or ours.",
    ],
  },
  {
    key: "painting", label: "Painting & Finishing",
    titles: ["Painter", "Interior & Exterior Painting", "Epoxy Flooring", "Waterproofing", "Wallpaper Installation", "Skim Coating & Putty Works", "Auto & Industrial Painting"],
    tags: ["Painting", "Finishing", "Epoxy", "Waterproofing", "Wallpaper", "Skim Coat", "Interior", "Exterior"],
    descriptions: [
      "Smooth, even finishes with proper surface prep and quality paint.",
      "Interior, exterior, epoxy, and waterproofing for homes and businesses.",
      "We protect your furniture and clean up after every job.",
    ],
  },
  {
    key: "welding", label: "Welding & Metalworks",
    titles: ["Welder", "Steel Fabrication", "Gate & Grills Fabrication", "Stainless Works", "Roll-up Door Installation", "Structural Steel Erection", "Railings & Stairs Fabrication"],
    tags: ["Welding", "Fabrication", "Steel", "Stainless", "Gates", "Grills", "Railings", "Metalworks"],
    descriptions: [
      "Custom metal fabrication — gates, grills, railings, and structural steel.",
      "On-site welding and shop fabrication with clean, strong joints.",
      "Design assistance available; we can match your reference photos.",
    ],
  },
  {
    key: "heavy-equipment", label: "Heavy Equipment & Operators",
    titles: ["Heavy Equipment Operator", "Backhoe Operator", "Crane Operator", "Forklift Operator", "Dump Truck Driver", "Equipment Rental with Operator", "Boom Truck Services"],
    tags: ["Heavy Equipment", "Operator", "Backhoe", "Crane", "Forklift", "Dump Truck", "Rental", "Hauling"],
    descriptions: [
      "Licensed operators with proven site experience and safety records.",
      "Equipment available with or without operator, per day or per project.",
      "We serve construction, demolition, and hauling requirements.",
    ],
  },
  {
    key: "hvac", label: "Aircon & Refrigeration (HVAC)",
    titles: ["Aircon Technician", "Aircon Cleaning", "Aircon Installation", "Aircon Repair", "Refrigeration Services", "Chiller & Cold Storage Maintenance", "HVAC Ducting"],
    tags: ["Aircon", "HVAC", "Cleaning", "Installation", "Repair", "Refrigeration", "Ducting", "Maintenance"],
    descriptions: [
      "Aircon cleaning, repair, and installation — window, split, and cassette types.",
      "Preventive maintenance contracts available for offices and shops.",
      "Genuine parts and freon top-up with proper diagnostics.",
    ],
  },
  {
    key: "professional", label: "Architecture & Engineering",
    titles: ["Architect", "Civil Engineer", "Structural Engineer", "Electrical Engineer", "Mechanical Engineer", "Surveyor", "Estimator / Quantity Surveyor", "Project Manager", "Interior Designer", "Building Permit Processing"],
    tags: ["Architecture", "Engineering", "Design", "Structural", "Permits", "Estimation", "Project Management", "Interior Design"],
    descriptions: [
      "Licensed professional services — design, plans, estimates, and supervision.",
      "We prepare signed-and-sealed drawings and assist with permits.",
      "Consultation, feasibility, and full project management available.",
    ],
  },
  {
    key: "it-digital", label: "IT & Digital Services",
    titles: ["Web Developer", "Full-Stack Developer", "Mobile App Developer", "Graphic Designer", "Virtual Assistant", "Social Media Manager", "IT Support / Technician", "Data Encoder", "Video Editor", "SEO Specialist"],
    tags: ["Web", "Development", "Design", "Virtual Assistant", "Social Media", "IT Support", "Video", "SEO"],
    descriptions: [
      "Reliable digital services with clear milestones and revisions.",
      "Portfolio available on request. Fixed-price or hourly engagements.",
      "Remote-friendly; I keep you updated at every stage.",
    ],
  },
  {
    key: "home-services", label: "Home Services & Cleaning",
    titles: ["House Cleaning", "General Cleaning", "Post-Construction Cleaning", "Housekeeper", "Pest Control", "Laundry Services", "Gardener / Landscaping", "Appliance Repair", "House Helper / Kasambahay"],
    tags: ["Cleaning", "Housekeeping", "Pest Control", "Laundry", "Gardening", "Landscaping", "Appliance Repair", "Home"],
    descriptions: [
      "Thorough, trustworthy home service with our own supplies.",
      "One-time or scheduled visits — deep cleaning to regular upkeep.",
      "Vetted, background-checked staff for your peace of mind.",
    ],
  },
  {
    key: "automotive", label: "Automotive & Mechanics",
    titles: ["Auto Mechanic", "Car Aircon Repair", "Auto Electrician", "Vulcanizing / Tire Services", "Car Wash & Detailing", "Motorcycle Mechanic", "Change Oil & PMS", "Towing Services"],
    tags: ["Automotive", "Mechanic", "Car Repair", "Aircon", "Detailing", "Tires", "Motorcycle", "PMS"],
    descriptions: [
      "Honest diagnostics and quality repairs for cars and motorcycles.",
      "Home-service mechanic available within the metro.",
      "We use quality parts and explain the work before we start.",
    ],
  },
  {
    key: "logistics", label: "Logistics & Hauling",
    titles: ["Delivery Rider", "Trucking Services", "Lipat Bahay / Moving", "Hauling Services", "Courier / Padala", "Warehouse Staff", "Driver (L300 / Truck)", "Cargo Forwarding"],
    tags: ["Logistics", "Delivery", "Trucking", "Moving", "Hauling", "Courier", "Driver", "Cargo"],
    descriptions: [
      "On-time delivery and hauling with careful handling.",
      "House moving with manpower and proper wrapping included.",
      "Per-trip or contract rates for regular routes.",
    ],
  },
  {
    key: "business", label: "Business & Admin Services",
    titles: ["Bookkeeper / Accountant", "Business Registration (DTI/SEC/BIR)", "Payroll Services", "Virtual Assistant", "Admin Staff", "Sales & Marketing", "Customer Service", "Encoder / Clerk"],
    tags: ["Bookkeeping", "Accounting", "Business Registration", "Payroll", "Admin", "Sales", "Marketing", "Customer Service"],
    descriptions: [
      "Keep your business compliant and organized — books, payroll, and filings.",
      "We handle DTI/SEC/BIR registration and renewals end-to-end.",
      "Flexible engagements: per task, part-time, or retainer.",
    ],
  },
  {
    key: "events-manpower", label: "Events & Manpower",
    titles: ["Event Staff", "Waiter / Server", "Bartender", "Security Guard", "Ushers / Hostess", "Event Setup Crew", "Sound & Lights", "Catering Services", "Manpower Agency"],
    tags: ["Events", "Manpower", "Waiter", "Bartender", "Security", "Setup", "Sound & Lights", "Catering"],
    descriptions: [
      "Reliable event manpower — trained, presentable, and on time.",
      "Per-event or per-day staffing for parties, launches, and corporate events.",
      "Full setup and teardown available on request.",
    ],
  },
  {
    key: "supplies", label: "Supplies & Materials",
    titles: ["Construction Materials", "Hardware Supply", "Electrical Supply", "Plumbing Supply", "Steel & Rebar Supply", "Aggregates (Sand & Gravel)", "Tools & Equipment", "Paint Supply", "Tiles & Sanitary Ware"],
    tags: ["Supplies", "Materials", "Hardware", "Construction Materials", "Steel", "Aggregates", "Tools", "Tiles"],
    descriptions: [
      "Quality materials at competitive prices with delivery available.",
      "Bulk and per-order supply for contractors and homeowners.",
      "Ask for our price list — we can quote per project.",
    ],
  },
];

export const CATEGORY_LABELS = POST_CATEGORIES.map((c) => c.label);

// v3.9.47 — shared suggestion sets for dumb-proofing the worker-profile form.
export const ALL_TITLES = Array.from(new Set(POST_CATEGORIES.flatMap((c) => c.titles))).sort();
export const COMMON_SKILLS = [
  "Electrical Wiring", "Plumbing", "Carpentry", "Masonry", "Welding", "Painting", "Tiling",
  "Troubleshooting", "Installation", "Preventive Maintenance", "Project Management", "AutoCAD",
  "Cost Estimation", "Safety Compliance", "Customer Service", "Bookkeeping", "Driving",
  "Heavy Equipment Operation", "Housekeeping", "Cooking", "Roofing", "Aircon Servicing",
  "Solar Installation", "CCTV Installation", "Landscaping", "Steel Fabrication",
];
export const WORKER_ABOUT_STARTERS = [
  "Licensed professional with hands-on experience across residential and commercial projects.",
  "Reliable, safety-focused and on time — I take pride in clean, quality work.",
  "Available for short-term jobs and long-term contracts across Metro Manila and nearby provinces.",
  "I bring my own tools and can provide references from past clients on request.",
];
export const YEARS_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8", "10", "12", "15", "20", "25", "30"];
export const NATIONALITIES = ["Filipino", "American", "Chinese", "Japanese", "Korean", "Indian", "British", "Australian", "Other"];

export function categoryByLabel(label: string): CategoryDef | undefined {
  const l = (label || "").trim().toLowerCase();
  return POST_CATEGORIES.find((c) => c.label.toLowerCase() === l || c.key === l);
}

const POSTING_TITLES_BY_CATEGORY: Record<string, { jobs: string[]; services: string[]; items: string[] }> = {
  construction: {
    jobs: ["Construction Foreman", "Construction Laborer", "Site Engineer"],
    services: ["General Construction", "Fit-out & Renovation", "Concrete Works"],
    items: ["Cement & Aggregates", "Rebar & Structural Steel", "Formwork Materials"],
  },
  electrical: {
    jobs: ["Electrician", "Electrical Technician", "Maintenance Electrician"],
    services: ["Electrical Installation", "House Rewiring", "Electrical Maintenance"],
    items: ["Electrical Wiring Materials", "Panel Boards & Breakers", "Lighting Fixtures"],
  },
  plumbing: {
    jobs: ["Plumber", "Plumbing Technician", "Pipe Fitter"],
    services: ["Plumbing Installation", "Pipe Repair & Leak Fixing", "Drainage Maintenance"],
    items: ["Pipes & Fittings", "Bathroom Fixtures", "Water Pumps & Tanks"],
  },
  carpentry: {
    jobs: ["Carpenter", "Mason", "Tile Setter"],
    services: ["Cabinet Making", "Drywall & Ceiling Works", "Tile Installation"],
    items: ["Lumber & Plywood", "Tiles & Adhesives", "Cabinet Hardware"],
  },
  painting: {
    jobs: ["Painter", "Finishing Worker", "Waterproofing Technician"],
    services: ["Interior & Exterior Painting", "Waterproofing", "Epoxy Flooring"],
    items: ["Paint & Primer", "Waterproofing Materials", "Painting Tools"],
  },
  welding: {
    jobs: ["Welder", "Metal Fabricator", "Welding Assistant"],
    services: ["Steel Fabrication", "Gate & Grills Fabrication", "Railings & Stairs Fabrication"],
    items: ["Steel Sections", "Welding Consumables", "Metal Sheets"],
  },
  "heavy-equipment": {
    jobs: ["Heavy Equipment Operator", "Backhoe Operator", "Forklift Operator"],
    services: ["Equipment Rental with Operator", "Excavation Services", "Crane & Lifting Services"],
    items: ["Heavy Equipment", "Equipment Parts", "Lifting Accessories"],
  },
  hvac: {
    jobs: ["Aircon Technician", "HVAC Technician", "Refrigeration Technician"],
    services: ["Aircon Cleaning", "Aircon Installation", "Aircon Repair"],
    items: ["Aircon Units", "HVAC Ducting Materials", "Refrigeration Parts"],
  },
  professional: {
    jobs: ["Architect", "Civil Engineer", "Estimator / Quantity Surveyor"],
    services: ["Architectural Design", "Engineering Plans", "Cost Estimation"],
    items: ["Surveying Equipment", "Drafting Supplies", "Measuring Instruments"],
  },
  "it-digital": {
    jobs: ["Web Developer", "Graphic Designer", "IT Support Technician"],
    services: ["Website Development", "Graphic Design", "IT Support Services"],
    items: ["Computers & Accessories", "Network Equipment", "Office Software Licenses"],
  },
  "home-services": {
    jobs: ["Housekeeper", "Cleaning Staff", "Gardener"],
    services: ["House Cleaning", "Post-Construction Cleaning", "Gardening & Landscaping"],
    items: ["Cleaning Supplies", "Garden Tools", "Laundry Equipment"],
  },
  automotive: {
    jobs: ["Auto Mechanic", "Auto Electrician", "Motorcycle Mechanic"],
    services: ["Vehicle Repair", "Car Aircon Repair", "Car Wash & Detailing"],
    items: ["Vehicle Parts", "Tires & Batteries", "Automotive Tools"],
  },
  logistics: {
    jobs: ["Delivery Rider", "Warehouse Staff", "Truck Driver"],
    services: ["Trucking Services", "Lipat Bahay / Moving", "Hauling Services"],
    items: ["Delivery Vehicles", "Warehouse Equipment", "Packaging Supplies"],
  },
  business: {
    jobs: ["Bookkeeper", "Admin Staff", "Customer Service Representative"],
    services: ["Bookkeeping & Accounting", "Payroll Services", "Business Registration Assistance"],
    items: ["Office Supplies", "Office Furniture", "Accounting Software Licenses"],
  },
  "events-manpower": {
    jobs: ["Event Staff", "Waiter / Server", "Event Setup Crew"],
    services: ["Event Staffing", "Catering Services", "Sound & Lights Setup"],
    items: ["Event Furniture", "Sound & Lighting Equipment", "Catering Equipment"],
  },
  supplies: {
    jobs: ["Inventory Assistant", "Procurement Assistant", "Storekeeper"],
    services: ["Construction Material Supply", "Hardware Supply", "Bulk Material Delivery"],
    items: ["Construction Materials", "Tools & Equipment", "Hardware Supplies"],
  },
};

// [posting.examples-and-suggestions](../../../docs/product/marketplace.md#posting-examples-and-suggestions): posting suggestions describe the actor's selected type.
// Unknown categories/types retain free entry; RFQs follow their explicit request type.
export function titlesForPosting(type: string, label: string, requestType = ""): string[] {
  const category = categoryByLabel(label);
  const choices = category ? POSTING_TITLES_BY_CATEGORY[category.key] : undefined;
  if (!choices) return [];
  if (type === "job") return choices.jobs;
  if (type === "service" || type === "company-service") return choices.services;
  if (type === "project") return choices.services.map((title) => `${title} Project`);
  if (type === "rfq") {
    const requested = requestType === "services" ? choices.services : requestType === "items" ? choices.items : [];
    return requested.map((title) => `Quotation for ${title}`);
  }
  return [];
}
export function tagsForCategory(label: string): string[] {
  return categoryByLabel(label)?.tags || [];
}

const JOB_SKILLS_BY_CATEGORY: Record<string, string[]> = {
  construction: ["Reading construction plans", "Site safety", "Concrete work"],
  electrical: ["Electrical wiring", "Reading wiring diagrams", "Troubleshooting"],
  plumbing: ["Pipe installation", "Leak detection", "Drain maintenance"],
  carpentry: ["Measuring and cutting", "Reading working drawings", "Tool handling"],
  painting: ["Surface preparation", "Paint application", "Finishing"],
  welding: ["Welding", "Metal fabrication", "Reading fabrication drawings"],
  "heavy-equipment": ["Equipment operation", "Pre-operation inspection", "Site safety"],
  hvac: ["Aircon installation", "Fault diagnosis", "Preventive maintenance"],
  professional: ["Technical drawings", "Cost estimation", "Project coordination"],
  "it-digital": ["Computer proficiency", "Technical problem solving", "Client communication"],
  "home-services": ["Cleaning techniques", "Safe product handling", "Attention to detail"],
  automotive: ["Vehicle diagnostics", "Preventive maintenance", "Tool handling"],
  logistics: ["Route planning", "Cargo handling", "Delivery coordination"],
  business: ["Record keeping", "Spreadsheet proficiency", "Customer communication"],
  "events-manpower": ["Guest assistance", "Event coordination", "Teamwork"],
  supplies: ["Inventory management", "Product knowledge", "Order handling"],
};
const GENERAL_JOB_SKILLS = ["Communication", "Teamwork", "Attention to detail", "Time management", "Problem solving", "Customer service"];

/** [posting.examples-and-suggestions](../../../docs/product/marketplace.md#posting-examples-and-suggestions): job skills start general, then follow an explicit category or a recognized role. */
export function jobSkillSuggestions(label: string, title: string): string[] {
  const role = title.trim().toLowerCase();
  const category = categoryByLabel(label) || (role ? POST_CATEGORIES.find((entry) =>
    [...(POSTING_TITLES_BY_CATEGORY[entry.key]?.jobs || []), ...entry.titles].some((option) => role.includes(option.toLowerCase())),
  ) : undefined);
  return category
    ? Array.from(new Set([...(JOB_SKILLS_BY_CATEGORY[category.key] || GENERAL_JOB_SKILLS), ...category.tags]))
    : GENERAL_JOB_SKILLS;
}
export function descriptionsForCategory(label: string): string[] {
  return categoryByLabel(label)?.descriptions || [];
}

/** Skills relevant to a selected role/headline, with the general set as a safe fallback. */
export function skillsForRole(role = "", headline = ""): string[] {
  const values = [role, headline].map((value) => value.trim().toLowerCase()).filter(Boolean);
  if (values.length === 0) return COMMON_SKILLS;

  const matching = POST_CATEGORIES.filter((category) =>
    category.titles.some((title) => values.some((value) => {
      const candidate = title.toLowerCase();
      return candidate === value || candidate.includes(value) || value.includes(candidate);
    })),
  );
  return matching.length > 0
    ? Array.from(new Set(matching.flatMap((category) => category.tags)))
    : COMMON_SKILLS;
}
